import { i18n } from '@/i18n/index.js';
import { intlLocale } from '@/utils/quran/locale';

const t = (...args) => i18n.global.t(...args);
const currentLocale = () => i18n.global.locale.value;

/** A piece of Quran data in the reader's language: { bs, en, de, … }, English when it is missing. A plain string stays as it is. */
export function localized(texts) {
  if (texts == null || typeof texts === 'string') return texts ?? '';
  return texts[currentLocale()] ?? texts.en ?? '';
}

/** A number written the way the reader's language writes it. */
export const formatNumber = (value) => new Intl.NumberFormat(intlLocale(currentLocale())).format(value);

/** A sentence for a failed request that isn't about one field; the server's own message when it sent one. */
export function requestError(error) {
  if (!error?.response) return t('study.errors.network');
  if (error.response.status === 429) return t('study.errors.tooMany');
  return error.response.data?.message && error.response.data.message !== 'error' ? error.response.data.message : t('study.errors.generic');
}

// Labels are getters: they read the current language whenever they are shown.
const cardKind = (id, look) => ({
  ...look,
  get label() {
    return t(`study.cardTypes.${id}.label`);
  },
  get short() {
    return t(`study.cardTypes.${id}.short`);
  },
});

/**
 * The study room's vocabulary. Card colours follow the order of evidence:
 * gold for the Qur'an, green for the Sunnah, blue for the scholars, grey
 * for the student's own words — the same everywhere a card appears.
 */
export const CARD_TYPES = {
  quran: cardKind('quran', { icon: 'solar:book-2-bold', bar: 'bg-[#d4a93c]', soft: 'bg-[#fbf3dc]', ring: 'border-[#e7cf8a]', text: 'text-[#8a6d24]' }),
  hadith: cardKind('hadith', { icon: 'solar:chat-square-like-bold', bar: 'bg-[#2f8f6a]', soft: 'bg-[#e6f4ee]', ring: 'border-[#a9d8c3]', text: 'text-[#22694d]' }),
  scholar: cardKind('scholar', { icon: 'solar:library-bold', bar: 'bg-[#3b6fb0]', soft: 'bg-[#e8eff8]', ring: 'border-[#b4c9e6]', text: 'text-[#2c5689]' }),
  root: cardKind('root', { icon: 'solar:leaf-bold', bar: 'bg-[#7b5fc7]', soft: 'bg-[#f1edfb]', ring: 'border-[#cbbcf0]', text: 'text-[#5a4398]' }),
  note: cardKind('note', { icon: 'solar:pen-new-round-bold', bar: 'bg-[#8a9a98]', soft: 'bg-[#f1f3f3]', ring: 'border-[#d3dad9]', text: 'text-[#55605f]' }),
};

export const cardType = (type) => CARD_TYPES[type] ?? CARD_TYPES.note;

const topicTemplate = (id, icon, lanes, colors) => ({
  id,
  icon,
  colors,
  get label() {
    return t(`study.templates.${id}.label`);
  },
  get hint() {
    return t(`study.templates.${id}.hint`);
  },
  get lanes() {
    return lanes.map((lane) => t(`study.templates.${id}.lanes.${lane}`));
  },
});

export const TEMPLATES = [
  topicTemplate('fiqh', 'solar:scale-linear', ['question', 'quran', 'sunnah', 'opinions', 'conclusion'], ['slate', 'gold', 'mint', 'sky', 'lilac']),
  topicTemplate('tafsir', 'solar:book-2-linear', ['ayah', 'words', 'occasion', 'tafsirs', 'lessons'], ['gold', 'slate', 'mint', 'sky', 'lilac']),
  topicTemplate('hadith', 'solar:chat-square-like-linear', ['text', 'sharh', 'rulings', 'lessons'], ['mint', 'sky', 'gold', 'lilac']),
  topicTemplate('free', 'solar:widget-add-linear', [], []),
];

export const template = (id) => TEMPLATES.find((t) => t.id === id) ?? TEMPLATES[3];

export const SPACE_COLORS = {
  teal: 'bg-deen-teal',
  gold: 'bg-[#d4a93c]',
  rose: 'bg-[#c4677a]',
  sky: 'bg-[#4a8fd0]',
  violet: 'bg-[#8268c4]',
  slate: 'bg-[#64748b]',
};

export const TASK_COLUMNS = ['todo', 'doing', 'done'].map((id) => ({
  id,
  get label() {
    return t(`study.taskColumns.${id}`);
  },
}));

/** "Ad-Duha 1–3" / "Ibn Kudama, El-Mugni" — the line under a card's text. */
export function cardCaption(card) {
  if (card.type === 'quran') return card.source;
  return [card.source, card.page ? t('study.card.page', { page: card.page }) : null].filter(Boolean).join(', ');
}

/** A card as plain text, for copying a topic into a paper. */
export function cardAsText(card) {
  const lines = [];
  if (card.title && card.type !== 'quran') lines.push(card.title);
  if (card.type === 'quran') {
    lines.push(card.arabic, card.translation);
  } else if (card.body) {
    lines.push(card.body);
  }
  const caption = cardCaption(card);
  if (caption) lines.push(`(${caption})`);
  if (card.note) lines.push(t('study.card.noteLine', { note: card.note }));
  return lines.filter(Boolean).join('\n');
}

/** Today in the browser's own calendar, as Y-m-d. */
export function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Browsers often lack Bosnian (see intlLocale), so its names are kept here; every other language comes from Intl.
const MONTHS = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
const WEEKDAYS = ['ned', 'pon', 'uto', 'sri', 'čet', 'pet', 'sub'];

/** "12. nov" for a Y-m-d date, read as a calendar day (no timezone shift). */
export function shortDate(ymd) {
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-').map(Number);
  const otherYear = y !== new Date().getFullYear();
  if (currentLocale() === 'bs') return `${d}. ${MONTHS[m - 1]}${otherYear ? ` ${y}` : ''}`;
  return new Intl.DateTimeFormat(intlLocale(currentLocale()), { day: 'numeric', month: 'short', ...(otherYear ? { year: 'numeric' } : {}) }).format(new Date(y, m - 1, d));
}

export function weekday(ymd) {
  const [y, m, d] = ymd.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  if (currentLocale() === 'bs') return WEEKDAYS[date.getDay()];
  return new Intl.DateTimeFormat(intlLocale(currentLocale()), { weekday: 'short' }).format(date);
}

/** Days from today to a Y-m-d date: 0 today, negative when past. */
export function daysUntil(ymd) {
  const [y, m, d] = ymd.split('-').map(Number);
  const now = new Date();
  return Math.round((new Date(y, m - 1, d) - new Date(now.getFullYear(), now.getMonth(), now.getDate())) / 86400000);
}

export function dueText(ymd) {
  if (!ymd) return '';
  const n = daysUntil(ymd);
  if (n < 0) return t('study.due.late', { n: -n });
  if (n === 0) return t('study.due.today');
  if (n === 1) return t('study.due.tomorrow');
  return n <= 7 ? t('study.due.in', { n }) : shortDate(ymd);
}

/** Sticky notes and frames on the canvas: paper colours for notes, a tint and an accent for frames. */
export const NOTE_COLORS = {
  gold: { paper: '#f7e6a8', ink: '#4a3b0c', dot: '#e3c25a' },
  mint: { paper: '#cdeedc', ink: '#173f2c', dot: '#69c39a' },
  rose: { paper: '#f6d2d8', ink: '#4e1f28', dot: '#e08a99' },
  sky: { paper: '#d3e6f8', ink: '#16324f', dot: '#6fa7dc' },
  lilac: { paper: '#e2d8f6', ink: '#2f2350', dot: '#9b86d8' },
  white: { paper: '#fffdf7', ink: '#1c4848', dot: '#d9d2bf' },
};

export const FRAME_COLORS = {
  gold: { accent: '#e3c25a', tint: 'rgba(227,194,90,0.07)', line: 'rgba(227,194,90,0.35)' },
  mint: { accent: '#69c39a', tint: 'rgba(105,195,154,0.07)', line: 'rgba(105,195,154,0.35)' },
  rose: { accent: '#e08a99', tint: 'rgba(224,138,153,0.07)', line: 'rgba(224,138,153,0.35)' },
  sky: { accent: '#6fa7dc', tint: 'rgba(111,167,220,0.07)', line: 'rgba(111,167,220,0.35)' },
  lilac: { accent: '#9b86d8', tint: 'rgba(155,134,216,0.08)', line: 'rgba(155,134,216,0.38)' },
  slate: { accent: '#9fb3b1', tint: 'rgba(159,179,177,0.06)', line: 'rgba(159,179,177,0.3)' },
  white: { accent: '#f3eee3', tint: 'rgba(243,238,227,0.05)', line: 'rgba(243,238,227,0.3)' },
};
